import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faLayerGroup,
  faMagnifyingGlass,
  faRotateRight,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SkeletonTable } from '@/components/skeletons/SkeletonTable';
import { CategoryBadge } from '@/components/communication/TemplateBadges';
import { CATEGORY_LABELS } from '@/components/communication/template-category.constants';
import { cn } from '@/lib/cn';
import {
  emailTemplateLibraryService,
  type AdoptionStatus,
  type TemplateAdoption,
} from '@/services/email-template-library.service';
import {
  ADOPTION_FILTERS,
  AdoptionBadge,
  DraftBadge,
  formatRelative,
} from './adoption-presentation';
import { TemplateAdoptionDrawer } from './TemplateAdoptionDrawer';

const SELECT_CLASS =
  'flex h-9 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Tenant Email Template Library — Phase 12.5C.
 *
 * Reads `GET /email-template-library`, the read-through view that merges the
 * platform masters with this tenant's rows. The page it replaces read
 * `GET /email-templates` — materialized rows only — and so reported
 * "No templates found" to every tenant that had never customized anything, while
 * all fifteen platform templates were in fact live for them (D-030).
 *
 * The empty state therefore distinguishes two genuinely different situations:
 * the platform has published nothing, versus the filters match nothing. It can
 * no longer say "no templates" merely because the tenant has no rows.
 */
export function TenantTemplateLibrary() {
  const [templates, setTemplates] = useState<TemplateAdoption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [adoption, setAdoption] = useState<AdoptionStatus | ''>('');

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTemplates(
        await emailTemplateLibraryService.list({
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(category ? { category } : {}),
        }),
      );
    } catch {
      setError('Could not load email templates. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  // Debounce the search; category applies immediately.
  useEffect(() => {
    const t = setTimeout(() => void load(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // Adoption is filtered client-side: the endpoint has no such query parameter,
  // and the master set is small and bounded.
  const visible = useMemo(
    () => (adoption ? templates.filter((t) => t.adoptionStatus === adoption) : templates),
    [templates, adoption],
  );

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of templates) counts.set(t.category, (counts.get(t.category) ?? 0) + 1);
    return counts;
  }, [templates]);

  const updateCount = useMemo(() => templates.filter((t) => t.updateAvailable).length, [templates]);

  const hasFilters = !!(search || category || adoption);
  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setAdoption('');
  };

  const selected = templates.find((t) => t.templateKey === selectedKey) ?? null;

  /** Replaces one row in place so the list reflects a mutation without a full refetch. */
  const applyUpdated = (next: TemplateAdoption) =>
    setTemplates((prev) => prev.map((t) => (t.templateKey === next.templateKey ? next : t)));

  return (
    <div className="space-y-4">
      {updateCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm text-foreground">
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3.5 w-3.5" aria-hidden />
          <span>
            {updateCount === 1
              ? 'A platform update is available for 1 customized template.'
              : `Platform updates are available for ${updateCount} customized templates.`}
          </span>
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={() => setAdoption('PLATFORM_UPDATE_AVAILABLE')}
          >
            Review
          </Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates…"
            aria-label="Search templates"
            className="pl-9"
          />
        </div>

        <select
          value={adoption}
          onChange={(e) => setAdoption(e.target.value as AdoptionStatus | '')}
          aria-label="Filter by state"
          className={SELECT_CLASS}
        >
          {ADOPTION_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>

        <Button variant="secondary" size="sm" onClick={() => void load()} title="Refresh">
          <FontAwesomeIcon icon={faRotateRight} className="h-3.5 w-3.5" />
          <span className="sr-only">Refresh</span>
        </Button>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Category sidebar — a dropdown below lg */}
        <aside className="lg:w-52 lg:shrink-0">
          <div className="lg:hidden">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Filter by category"
              className={cn(SELECT_CLASS, 'w-full')}
            >
              <option value="">All categories</option>
              {Object.keys(CATEGORY_LABELS).map((key) => (
                <option key={key} value={key}>
                  {CATEGORY_LABELS[key]}
                </option>
              ))}
            </select>
          </div>

          <nav className="hidden rounded-xl border border-border bg-card p-2 lg:block">
            <p className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Categories
            </p>
            <button
              type="button"
              onClick={() => setCategory('')}
              className={cn(
                'flex min-h-[36px] w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                category === '' ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
              )}
            >
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" />
                All
              </span>
              <span className="text-xs text-muted-foreground">{templates.length}</span>
            </button>
            {/* Driven by the data, not by a hardcoded list, so a category the
                shared constants do not yet label (e.g. PLATFORM) still appears
                and is still filterable. */}
            {[...categoryCounts.keys()].sort().map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(key)}
                className={cn(
                  'flex min-h-[36px] w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                  category === key ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                )}
              >
                <span className="truncate">{CATEGORY_LABELS[key] ?? key}</span>
                <span className="text-xs text-muted-foreground">
                  {categoryCounts.get(key) ?? 0}
                </span>
              </button>
            ))}
          </nav>
        </aside>

        <section className="min-w-0 flex-1">
          {loading ? (
            <SkeletonTable rows={8} cols={5} />
          ) : error ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/30 bg-destructive/5 py-16 text-center">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-6 w-6 text-destructive" />
              <p className="mt-3 text-sm text-foreground">{error}</p>
              <Button variant="secondary" size="sm" className="mt-4" onClick={() => void load()}>
                Retry
              </Button>
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-16 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5 text-muted-foreground" />
              </div>
              {hasFilters ? (
                <>
                  <p className="mt-3 text-sm font-medium text-foreground">
                    No templates match these filters
                  </p>
                  <Button variant="secondary" size="sm" className="mt-4" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </>
              ) : (
                <>
                  {/* Reached only when the PLATFORM has published nothing — never
                      merely because this workspace has no rows of its own. */}
                  <p className="mt-3 text-sm font-medium text-foreground">
                    No email templates have been published yet
                  </p>
                  <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                    Your service provider maintains these templates. They will appear here once
                    published.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2.5 font-medium">Template</th>
                      <th className="px-4 py-2.5 font-medium">Category</th>
                      <th className="px-4 py-2.5 font-medium">State</th>
                      <th className="px-4 py-2.5 font-medium">Version</th>
                      <th className="px-4 py-2.5 font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {visible.map((t) => (
                      <tr
                        key={t.templateKey}
                        onClick={() => setSelectedKey(t.templateKey)}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-foreground">{t.name}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">
                            {t.templateKey}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <CategoryBadge category={t.category} />
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <AdoptionBadge status={t.adoptionStatus} />
                            {t.hasUnpublishedChanges && <DraftBadge />}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                          {/* Platform version is always meaningful; the tenant's own
                              version exists only once it has published something. */}
                          <span title="Platform version">P v{t.platformVersion ?? '—'}</span>
                          {t.isOverride && (
                            <span title="Your version" className="ml-2 text-primary">
                              You v{t.tenantVersion ?? '—'}
                            </span>
                          )}
                        </td>
                        <td
                          className="px-4 py-2.5 text-xs text-muted-foreground"
                          title={
                            t.tenantUpdatedDate
                              ? new Date(t.tenantUpdatedDate).toLocaleString()
                              : undefined
                          }
                        >
                          {formatRelative(t.tenantUpdatedDate ?? t.platformReleaseDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-2 md:hidden">
                {visible.map((t) => (
                  <button
                    key={t.templateKey}
                    type="button"
                    onClick={() => setSelectedKey(t.templateKey)}
                    className="flex min-h-[44px] w-full flex-col gap-2 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted/40"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{t.name}</div>
                      <div className="truncate font-mono text-[11px] text-muted-foreground">
                        {t.templateKey}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <AdoptionBadge status={t.adoptionStatus} />
                      {t.hasUnpublishedChanges && <DraftBadge />}
                      <CategoryBadge category={t.category} />
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      <TemplateAdoptionDrawer
        template={selected}
        onClose={() => setSelectedKey(null)}
        onChanged={applyUpdated}
        onReload={() => void load()}
      />
    </div>
  );
}
